import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import routes, { routeAllowed, homePath } from "routes.js";
import { useAuth, organisationLabel } from "contexts/AuthContext";
import { usePlatformStatus } from "contexts/PlatformStatusContext";
import { MaintenanceBanner } from "components/admin/PlatformNotices";
import { RoleAccentBar, RoleNotice } from "components/role/RoleSwitcher";
import { onWrongRole } from "lib/apiClient";
import { takeSwitchNotice } from "lib/seats";

export default function PortalLayout(props) {
  const { ...rest } = props;
  const location = useLocation();
  const { maintenance } = usePlatformStatus();
  const { isPartner, portal } = useAuth();
  const home = homePath(isPartner);
  const [open, setOpen] = React.useState(() => window.innerWidth >= 1280);
  const [currentRoute, setCurrentRoute] = React.useState("Dashboard");
  const closeSidebar = React.useCallback(() => setOpen(false), []);
  const [notice, setNotice] = React.useState(() => takeSwitchNotice());
  const dismissNotice = React.useCallback(() => setNotice(null), []);
  React.useEffect(() => onWrongRole((text) => setNotice({ kind: null, text })), []);
  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return window.localStorage.getItem("wasaa_sidebar_collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleCollapse = React.useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("wasaa_sidebar_collapsed", next ? "1" : "0");
      } catch {
        // localStorage unavailable — the toggle still works for this page load.
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    function handleResize() {
      setOpen(window.innerWidth >= 1280);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  React.useEffect(() => {
    getActiveRoute(routes);
    if (window.innerWidth < 1280) setOpen(false);
  }, [location.pathname]);

  const getActiveRoute = (routes) => {
    let activeRoute = "Dashboard";
    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(
          routes[i].layout + "/" + routes[i].path
        ) !== -1
      ) {
        setCurrentRoute(routes[i].name);
      }
    }
    return activeRoute;
  };
  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/portal") {
        return (
          <Route
            path={`/${prop.path}`}
            element={routeAllowed(prop, isPartner) ? prop.component : <Navigate to={home} replace />}
            key={key}
          />
        );
      } else {
        return null;
      }
    });
  };

  document.documentElement.dir = "ltr";
  return (
    <div className="min-h-screen w-full bg-background text-foreground">
      <RoleAccentBar />
      <RoleNotice notice={notice} onDismiss={dismissNotice} />
      <Navbar
        onOpenSidenav={() => setOpen(true)}
        logoText={organisationLabel(portal) || "Wasaa Markets Portal"}
        brandText={currentRoute}
        {...rest}
      />
      {/* Kuest admin body: container grid, 200px sidebar + content column. */}
      <main className="mx-auto w-full max-w-[1332px] px-4 pb-4 pt-2 lg:pb-8 lg:pt-4">
        <div
          className={`grid min-w-0 gap-8 ${
            collapsed ? "xl:grid-cols-[44px_minmax(0,1fr)] xl:gap-10" : "xl:grid-cols-[200px_minmax(0,1fr)] xl:gap-16"
          }`}
        >
          <Sidebar open={open} onClose={closeSidebar} collapsed={collapsed} onToggleCollapse={toggleCollapse} />
          <div className="min-w-0">
            <div className="mb-6">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">{currentRoute}</h1>
            </div>
            <div className="mb-auto min-h-[70vh]">
              {maintenance?.active && <MaintenanceBanner message={maintenance.message} />}
              <Routes>
                {getRoutes(routes)}

                <Route
                  path="/"
                  element={<Navigate to={home} replace />}
                />
              </Routes>
            </div>
            <Footer />
          </div>
        </div>
      </main>
    </div>
  );
}
